import { useCallback, useEffect, useMemo, useState } from 'react';
import { Organization } from '../../../models/organization';
import { startIntelligenceTrial } from '../../../api/paymentsServer';
import {
  IntelligencePayMethod,
  purchaseIntelligenceCapacity,
  purchaseIntelligenceCreditPack,
} from '../../../api/intelligencePayments';
import { listExchangeSourcesForPair } from '../../../api/exchangeSources';
import { IntelligenceProductStatus } from '../../../api/ai';
import { useLoading } from '../../../hooks/useLoading';
import { useConfirmationDialog } from '../../../hooks/useConfirmationDialog';
import { ExchangeSource } from '../../../models/exchangeSource';
import { CONFIRMATION_SECONDARY_BLOCK_DELIMITER } from '../../../utils/confirmationDialogMessage';
import {
  IntelligencePurchase,
  allowedPayMethodsFromStatus,
  catalogFromStatus,
  defaultIntelPayMethod,
} from './hireTypes';

export interface UseIntelligenceHireOptions {
  organizationId: string;
  organization: Organization | null;
  status: IntelligenceProductStatus | null;
  onSuccess: (message: string) => void;
  onActivated?: () => void | Promise<void>;
}

export const useIntelligenceHire = ({
  organizationId,
  organization,
  status,
  onSuccess,
  onActivated,
}: UseIntelligenceHireOptions) => {
  const { showLoading, hideLoading } = useLoading();
  const { showConfirmation } = useConfirmationDialog();
  const [payMethod, setPayMethod] = useState<IntelligencePayMethod>('ves');
  const [packQty, setPackQty] = useState(1);
  const [purchase, setPurchase] = useState<IntelligencePurchase | null>(null);
  const [vesCaptureFile, setVesCaptureFile] = useState<File | null>(null);
  const [vesExchangeRate, setVesExchangeRate] = useState(0);
  const [vesExchangeSources, setVesExchangeSources] = useState<ExchangeSource[]>(
    []
  );
  const [vesError, setVesError] = useState<string | null>(null);

  const allowedPayMethods = useMemo(
    () => allowedPayMethodsFromStatus(status),
    [status]
  );
  const catalog = useMemo(() => catalogFromStatus(status), [status]);
  const pendingVes = status?.pendingVes ?? [];
  const pendingPack = pendingVes.find(p => p.product === 'intelligence_pack');
  const pendingCapacity = pendingVes.find(p => p.product === 'intelligence');
  const isCapacitySwitch =
    purchase?.kind === 'capacity' &&
    Boolean(status?.capacityId) &&
    !status?.trialing &&
    status?.capacityId !== purchase.capacityId;

  const purchaseAmountUSD = useMemo(() => {
    if (!purchase) return 0;
    if (purchase.kind === 'pack') {
      const qty = Math.max(1, Math.min(50, Math.floor(packQty) || 1));
      return purchase.unitPrice * qty;
    }
    return purchase.unitPrice;
  }, [purchase, packQty]);

  useEffect(() => {
    if (!purchase) return;
    let cancelled = false;
    (async () => {
      try {
        const sources = await listExchangeSourcesForPair({
          fromCurrency: 'USD',
          toCurrency: 'VES',
        });
        const dolarParalelo = sources.filter(s => s.name === 'Dolar Paralelo');
        if (cancelled) return;
        setVesExchangeSources(dolarParalelo);
        if (dolarParalelo.length > 0) {
          const primary =
            dolarParalelo.find(s => s.rate > 0) ?? dolarParalelo[0];
          setVesExchangeRate(primary.rate);
        } else {
          setVesExchangeRate(0);
        }
      } catch {
        if (!cancelled) {
          setVesExchangeSources([]);
          setVesExchangeRate(0);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [purchase]);

  const closePurchase = useCallback(() => {
    setPurchase(null);
    setVesCaptureFile(null);
    setVesError(null);
    setPackQty(1);
  }, []);

  const openPurchase = useCallback(
    (draft: IntelligencePurchase) => {
      setPurchase(draft);
      setPackQty(1);
      setPayMethod(defaultIntelPayMethod(allowedPayMethods));
      setVesCaptureFile(null);
      setVesError(null);
    },
    [allowedPayMethods]
  );

  const afterSuccess = useCallback(
    async (message: string) => {
      onSuccess(message);
      await onActivated?.();
    },
    [onSuccess, onActivated]
  );

  const openCapacity = useCallback(
    (capacityId: 'lite' | 'plus' | 'extra') => {
      if (status?.capacityId === capacityId && !status?.trialing) return;
      if (pendingCapacity) {
        onSuccess(
          'Ya tienes un pago de Intelligence en revisión. Espera la aprobación o el rechazo.'
        );
        return;
      }
      const item = catalog.find(c => c.id === capacityId);
      const unitPrice =
        status?.bundleOffer?.capacityId === capacityId &&
        typeof status.bundleOffer.discountedPrice === 'number'
          ? status.bundleOffer.discountedPrice
          : typeof item?.monthlyPrice === 'number'
            ? item.monthlyPrice
            : 0;
      openPurchase({
        kind: 'capacity',
        capacityId,
        name: item?.name || `Intelligence ${capacityId.toUpperCase()}`,
        unitPrice,
        credits: item?.monthlyCredits,
      });
    },
    [status, pendingCapacity, catalog, openPurchase, onSuccess]
  );

  const openPack = useCallback(
    (pack: { id: string; name: string; price: number; credits: number }) => {
      if (pendingPack) {
        onSuccess(
          'Ya tienes un paquete de créditos en revisión. Espera la aprobación o el rechazo.'
        );
        return;
      }
      openPurchase({
        kind: 'pack',
        packId: pack.id,
        name: pack.name,
        unitPrice: Number(pack.price) || 0,
        credits: Number(pack.credits) || 0,
      });
    },
    [pendingPack, openPurchase, onSuccess]
  );

  const runCapacityStripe = useCallback(
    async (capacityId: 'lite' | 'plus' | 'extra') => {
      if (!organization?.billing?.stripeSubscriptionId) {
        setVesError(
          'Necesitas un plan MayorX activo con Stripe. Intelligence se agrega a esa suscripción; no abre un checkout nuevo.'
        );
        return;
      }
      showLoading('Agregando Intelligence a tu suscripción...', 'overlay');
      try {
        const result = await purchaseIntelligenceCapacity({
          organizationId,
          capacityId,
          paymentMethod: 'stripe',
        });
        const redirectUrl = result.checkoutUrl || result.url;
        if (redirectUrl) {
          window.location.href = redirectUrl;
          return;
        }
        hideLoading();
        const label = result.capacityName || purchase?.name || capacityId;
        closePurchase();
        if (result.mode === 'pending_invoice') {
          await afterSuccess(
            `${label} se agregó a tu suscripción. Intelligence se activa cuando Stripe confirme el pago.`
          );
          return;
        }
        await afterSuccess(`${label} activado en tu suscripción actual`);
      } catch (error: any) {
        hideLoading();
        const detail =
          error?.response?.data?.detail ||
          error?.message ||
          'No se pudo activar Intelligence';
        setVesError(
          typeof detail === 'string' ? detail : 'No se pudo activar Intelligence'
        );
      }
    },
    [
      organization?.billing?.stripeSubscriptionId,
      organizationId,
      purchase?.name,
      showLoading,
      hideLoading,
      closePurchase,
      afterSuccess,
    ]
  );

  const runPackStripe = useCallback(
    async (packId: string, quantity: number, amountUSD: number) => {
      if (
        !organization?.billing?.stripeCustomerId &&
        !organization?.billing?.stripeSubscriptionId
      ) {
        setVesError(
          'Necesitas facturación Stripe activa para comprar créditos con tarjeta.'
        );
        return;
      }
      const qty = Math.max(1, Math.min(50, Math.floor(quantity) || 1));
      showLoading('Abriendo compra de créditos...', 'overlay');
      try {
        const result = await purchaseIntelligenceCreditPack({
          organizationId,
          packId,
          quantity: qty,
          paymentMethod: 'stripe',
          amountUSD,
        });
        const redirectUrl = result.checkoutUrl || result.url;
        if (redirectUrl) {
          window.location.href = redirectUrl;
          return;
        }
        hideLoading();
        closePurchase();
        await afterSuccess('Compra de créditos iniciada');
      } catch (error: any) {
        hideLoading();
        const detail =
          error?.response?.data?.detail?.message ||
          error?.response?.data?.detail ||
          error?.message ||
          'No se pudieron comprar créditos';
        setVesError(
          typeof detail === 'string' ? detail : 'No se pudieron comprar créditos'
        );
      }
    },
    [
      organization?.billing?.stripeCustomerId,
      organization?.billing?.stripeSubscriptionId,
      organizationId,
      showLoading,
      hideLoading,
      closePurchase,
      afterSuccess,
    ]
  );

  const submitVes = useCallback(async () => {
    if (!purchase) return;
    if (!vesCaptureFile) {
      setVesError('Sube la captura de la transacción.');
      return;
    }
    if (!vesExchangeRate || vesExchangeRate <= 0) {
      setVesError('Indica un tipo de cambio válido.');
      return;
    }
    const primarySource =
      vesExchangeSources.find(s => s.rate === vesExchangeRate) ??
      vesExchangeSources[0];
    showLoading('Enviando solicitud de pago en Bolívares...', 'overlay');
    setVesError(null);
    try {
      if (purchase.kind === 'capacity' && purchase.capacityId) {
        await purchaseIntelligenceCapacity({
          organizationId,
          capacityId: purchase.capacityId,
          paymentMethod: 'ves',
          exchangeRate: vesExchangeRate,
          exchangeSourceId: primarySource?.id,
          exchangeSourceName: primarySource?.name,
          captureFile: vesCaptureFile,
          amountUSD: purchaseAmountUSD,
        });
        await afterSuccess(
          `Solicitud enviada: ${purchase.name}. Intelligence se activará cuando se apruebe el pago.`
        );
      } else if (purchase.kind === 'pack' && purchase.packId) {
        const qty = Math.max(1, Math.min(50, Math.floor(packQty) || 1));
        await purchaseIntelligenceCreditPack({
          organizationId,
          packId: purchase.packId,
          quantity: qty,
          paymentMethod: 'ves',
          exchangeRate: vesExchangeRate,
          exchangeSourceId: primarySource?.id,
          exchangeSourceName: primarySource?.name,
          captureFile: vesCaptureFile,
          amountUSD: purchaseAmountUSD,
        });
        await afterSuccess(
          `Solicitud enviada: ${qty}× ${purchase.name} (${purchaseAmountUSD.toFixed(2)} USD). Los créditos se acreditarán cuando se apruebe el pago.`
        );
      }
      closePurchase();
      hideLoading();
    } catch (err: any) {
      hideLoading();
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        'Error al enviar la solicitud.';
      setVesError(
        typeof detail === 'string' ? detail : 'Error al enviar la solicitud.'
      );
    }
  }, [
    purchase,
    vesCaptureFile,
    vesExchangeRate,
    vesExchangeSources,
    organizationId,
    purchaseAmountUSD,
    packQty,
    showLoading,
    hideLoading,
    closePurchase,
    afterSuccess,
  ]);

  const confirmPurchase = useCallback(() => {
    if (!purchase) return;
    if (payMethod === 'ves') {
      void submitVes();
      return;
    }
    if (purchase.kind === 'capacity' && purchase.capacityId) {
      void runCapacityStripe(purchase.capacityId);
      return;
    }
    if (purchase.kind === 'pack' && purchase.packId) {
      void runPackStripe(purchase.packId, packQty, purchaseAmountUSD);
    }
  }, [
    purchase,
    payMethod,
    packQty,
    purchaseAmountUSD,
    submitVes,
    runCapacityStripe,
    runPackStripe,
  ]);

  const startTrial = useCallback(() => {
    showConfirmation(
      {
        title: 'Probar Intelligence gratis',
        message: `Tendrás Intelligence Plus por 14 días sin cargo (~1,500 créditos). Al terminar el periodo de prueba puedes suscribirte.${CONFIRMATION_SECONDARY_BLOCK_DELIMITER}Una sola prueba por organización.`,
        sentiment: 'info',
        confirmText: 'Empezar prueba',
        cancelText: 'Cancelar',
      },
      async () => {
        showLoading('Activando prueba de Intelligence...', 'overlay');
        try {
          await startIntelligenceTrial({ organizationId });
          hideLoading();
          await afterSuccess('Prueba de Intelligence activada');
        } catch (error: any) {
          hideLoading();
          const detail =
            error?.response?.data?.detail?.message ||
            error?.response?.data?.detail ||
            error?.message;
          onSuccess(
            typeof detail === 'string'
              ? detail
              : 'No se pudo iniciar la prueba de Intelligence'
          );
        }
      }
    );
  }, [
    organizationId,
    showConfirmation,
    showLoading,
    hideLoading,
    afterSuccess,
    onSuccess,
  ]);

  return {
    allowedPayMethods,
    catalog,
    payMethod,
    setPayMethod,
    packQty,
    setPackQty,
    purchase,
    purchaseAmountUSD,
    vesCaptureFile,
    setVesCaptureFile,
    vesExchangeRate,
    setVesExchangeRate,
    vesExchangeSources,
    vesError,
    setVesError,
    isCapacitySwitch,
    pendingVes,
    pendingPack,
    pendingCapacity,
    closePurchase,
    openCapacity,
    openPack,
    confirmPurchase,
    startTrial,
    currentCapacityId: status?.capacityId ?? null,
  };
};

export type IntelligenceHireApi = ReturnType<typeof useIntelligenceHire>;
